/**
 * Pure show-condition logic for the first-run push notification primer.
 *
 * Kept framework-free so it can be unit-tested without TestBed (the repo's
 * vitest can't resolve external templateUrl/styleUrl — see MEMORY.md). The
 * component injects services and feeds their plain values into these helpers.
 */

/** localStorage key holding the user's prior decision (dismissed | enabled). */
export const PUSH_PRIMER_STORAGE_KEY = 'imp.push-primer';

/** Persisted decision shape. */
export interface PushPrimerDecision {
  state: 'dismissed' | 'enabled';
  at: string; // ISO timestamp
}

/** Inputs to the show-condition gate — all plain, already-read values. */
export interface PushPrimerConditions {
  /** PushRegistrationService.supported() */
  supported: boolean;
  /** Browser Notification.permission. */
  permission: NotificationPermission;
  /** Prior persisted decision, or null if none. */
  priorDecision: PushPrimerDecision | null;
  /**
   * The user's saved global push preference, when cheaply known.
   *   true     — push on
   *   false    — push explicitly OFF (suppress the primer)
   *   undefined — unknown / not loaded (don't block on it)
   */
  globalPushPref: boolean | undefined;
  /**
   * Whether the first-run sheet is on screen. SUPPRESSES the primer outright.
   *
   * The two surfaces ask the same question at the same moment, and first run
   * asks it better: its `alerts` stage ("What reaches you") states what each
   * choice actually does, where the primer is a bare yes/no. Worse, the primer
   * is a non-modal card in the bottom-right corner, so it lands ON TOP of the
   * sheet's own footer and covers the primary button.
   *
   * This was previously left to a 5s timer whose comment said it existed "so it
   * doesn't compete with first paint / onboarding". A timer cannot win that
   * race — first run is a two-minute flow — so the primer competed on every
   * single new-user session, which is precisely the population it targets.
   */
  firstRunOpen: boolean;
  /**
   * Whether some surface has claimed the bottom-right corner
   * (`ShellStateService.bottomAnchorClaimed()`). SUPPRESSES the primer, for
   * the same reason `firstRunOpen` does: the primer is a 360px card fixed to
   * that corner, and what a claiming surface puts there is its composer's
   * Send button. The assistant claims it from both the /ai page and the
   * docked panel, so "ask the AI to book me a desk" ended at a card sitting
   * on top of the control that would have sent the message.
   *
   * Deliberately not a route check: the primer has no reason to know the
   * router, and a list of paths is wrong the moment a fourth surface anchors
   * something down there.
   */
  bottomAnchorClaimed: boolean;
}

/**
 * Whether the primer should appear. All conditions must hold:
 *   - the first-run sheet is not on screen,
 *   - nothing has claimed the bottom-right corner,
 *   - push is supported in this browser,
 *   - the OS-level permission is still 'default' (never when granted/denied),
 *   - no prior decision was persisted,
 *   - the saved push preference is not explicitly OFF.
 */
export function shouldShowPushPrimer(c: PushPrimerConditions): boolean {
  if (c.firstRunOpen) return false;
  if (c.bottomAnchorClaimed) return false;
  if (!c.supported) return false;
  if (c.permission !== 'default') return false;
  if (c.priorDecision !== null) return false;
  if (c.globalPushPref === false) return false;
  return true;
}

/**
 * Parse a persisted decision from a raw localStorage string. Tolerant of
 * malformed/legacy values — returns null rather than throwing so a corrupt
 * entry simply means "no decision recorded" (the primer can show again).
 */
export function parsePushPrimerDecision(raw: string | null): PushPrimerDecision | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === 'object' &&
      'state' in parsed &&
      ((parsed as PushPrimerDecision).state === 'dismissed' ||
        (parsed as PushPrimerDecision).state === 'enabled')
    ) {
      return parsed as PushPrimerDecision;
    }
    return null;
  } catch {
    return null;
  }
}

/** Build the decision record to persist for a given outcome. */
export function buildPushPrimerDecision(
  state: PushPrimerDecision['state'],
  now: Date = new Date(),
): PushPrimerDecision {
  return { state, at: now.toISOString() };
}
